import { useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { Gate } from '../../../global/contexts/auth/auth';
import { IconCalculator, IconChart, IconEdit, IconLock, IconReceipt } from '../../../common/icons';
import { useClient, useClientHoldings } from '../contexts/clients';
import ClientFarmsPanel from './farms-panel';
import DonorsTab from './donors-tab';
import RecipientsTab from './recipients-tab';
import SessionsTab from './sessions-tab';
import ClientUserForm from '../forms/client-user';

import '../styles/detail.css';

const STATUS_LABEL = { 2: 'Activo', 1: 'Inactivo', 0: 'Eliminado' };
const TAB_KEYS = ['donadoras', 'receptoras', 'sesiones', 'fincas'];

function Field({ label, value }) {
    return (
        <div className="client-detail__field">
            <span className="client-detail__field-label">{label}</span>
            <span className="client-detail__field-value">
                {value || <span className="client-detail__empty">—</span>}
            </span>
        </div>
    );
}

// Barra de pestañas accesible (APG tabs): role=tablist/tab, roving tabindex, flechas + Home/End.
function ClientTabs({ tabs, active, onChange }) {
    const refs = useRef([]);
    const idx = Math.max(0, tabs.findIndex((t) => t.key === active));
    const onKeyDown = (e) => {
        const keys = { ArrowRight: (idx + 1) % tabs.length, ArrowLeft: (idx - 1 + tabs.length) % tabs.length, Home: 0, End: tabs.length - 1 };
        if (!(e.key in keys)) return;
        e.preventDefault();
        const next = keys[e.key];
        onChange(tabs[next].key);
        refs.current[next]?.focus();
    };
    return (
        <div className="client-tabs" role="tablist" aria-label="Secciones del cliente" onKeyDown={onKeyDown}>
            {tabs.map((t, i) => (
                <button
                    key={t.key}
                    ref={(el) => { refs.current[i] = el; }}
                    type="button"
                    role="tab"
                    id={`ctab-${t.key}`}
                    aria-selected={active === t.key}
                    aria-controls={`ctabpanel-${t.key}`}
                    tabIndex={active === t.key ? 0 : -1}
                    className={`client-tab ${active === t.key ? 'client-tab--active' : ''}`}
                    onClick={() => onChange(t.key)}
                >
                    {t.label}{t.count != null && <span className="client-tab__count">{t.count}</span>}
                </button>
            ))}
        </div>
    );
}

export default function ClientDetail({ clientId }) {
    const { data: client, isLoading, error } = useClient(clientId);
    const { data: holdings, isLoading: holdingsLoading, error: holdingsError } = useClientHoldings(clientId);
    const [params, setParams] = useSearchParams();
    const [invite, setInvite] = useState(false);

    if (isLoading) return <div className="client-detail__state">Cargando cliente…</div>;
    if (error || !client) return <div className="client-detail__state">Cliente no encontrado.</div>;

    const remarks = typeof client.remarks === 'string' ? client.remarks : '';
    const donors = holdings?.donors?.items ?? [];
    const recipients = holdings?.recipients?.items ?? [];

    const active = TAB_KEYS.includes(params.get('tab')) ? params.get('tab') : 'donadoras';
    const setTab = (key) => {
        const next = new URLSearchParams(params);
        next.set('tab', key);
        setParams(next, { replace: true });
    };
    const tabs = [
        { key: 'donadoras',  label: 'Donadoras',  count: holdingsLoading ? null : donors.length },
        { key: 'receptoras', label: 'Receptoras', count: holdingsLoading ? null : recipients.length },
        { key: 'sesiones',   label: 'Sesiones' },
        { key: 'fincas',     label: 'Fincas' },
    ];

    return (
        <div className="client-detail">
            <header className="client-detail__header">
                <div className="client-detail__heading">
                    <Link to="/admin/clients" className="client-detail__back">Clientes</Link>
                    <div className="client-detail__title-row">
                        <h1 className="client-detail__title">{client.name}</h1>
                        <span className={`client-badge client-badge--${client.status}`}>{STATUS_LABEL[client.status] || '—'}</span>
                    </div>
                </div>
                <div className="client-detail__actions">
                    <Gate domain="kpis" action="read">
                        <Link to={`/admin/clients/${client.id}/stats`} className="client-detail__edit"><IconChart /><span>Estadísticas</span></Link>
                    </Gate>
                    <Gate domain="billing" action="read">
                        <Link to={`/admin/clients/${client.id}/liquidation`} className="client-detail__edit"><IconReceipt /><span>Liquidar</span></Link>
                    </Gate>
                    <Gate domain="billing" action="read">
                        <Link to={`/admin/clients/${client.id}/account`} className="client-detail__edit"><IconCalculator /><span>Estado de cuenta</span></Link>
                    </Gate>
                    <Gate domain="clients" action="write">
                        <button type="button" className="client-detail__edit" onClick={() => setInvite(true)}><IconLock /><span>Usuario y acceso</span></button>
                    </Gate>
                    <Gate domain="clients" action="write">
                        <Link to={`/admin/clients/${client.id}/edit`} className="client-detail__edit"><IconEdit /><span>Editar</span></Link>
                    </Gate>
                </div>
            </header>

            <section className="client-detail__card">
                <h2 className="client-detail__section-title">Información</h2>
                <div className="client-detail__grid">
                    <Field label="Documento" value={client.document} />
                    <Field label="Teléfono" value={client.phone} />
                    <Field label="Correo" value={client.email} />
                    <Field label="Estado" value={STATUS_LABEL[client.status]} />
                </div>
                {remarks && (
                    <div className="client-detail__notes">
                        <span className="client-detail__field-label">Notas</span>
                        <p>{remarks}</p>
                    </div>
                )}
            </section>

            <ClientTabs tabs={tabs} active={active} onChange={setTab} />

            <div role="tabpanel" id={`ctabpanel-${active}`} aria-labelledby={`ctab-${active}`}>
                {active === 'donadoras' && <DonorsTab donors={donors} isLoading={holdingsLoading} error={holdingsError} />}
                {active === 'receptoras' && <RecipientsTab recipients={recipients} clientId={clientId} isLoading={holdingsLoading} error={holdingsError} />}
                {active === 'sesiones' && <SessionsTab donors={donors} recipients={recipients} />}
                {active === 'fincas' && <ClientFarmsPanel client={client} />}
            </div>

            {invite && <ClientUserForm client={client} onClose={() => setInvite(false)} />}
        </div>
    );
}
